/* eslint-disable react-refresh/only-export-components */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSessionStorage } from "usehooks-ts";

import { useGetProfileCompletion } from "$/api/user";
import { useAuth } from "$/lib/providers/AuthProvider";
import type { ProfileCompletionData } from "$/lib/types/models/User.types";

export type ProfileTab = "general" | "socialSecurity" | "fiscal" | "accounting";

type ProfileContext = {
  minimumYear: number;
  lastVisitedTab: ProfileTab;
  lastVisitedYearInTab: Record<ProfileTab, number>;
  profileCompletion: ProfileCompletionData | undefined;
  profileCompletionPercentage: number;
  isFetchingProfileCompletion: boolean;
  setLastVisitedTab: (tab: ProfileTab) => void;
  setLastVisitedYearInTab: (tab: ProfileTab, year: number) => void;
  resetProfile: () => void;
};

const ProfileContext = createContext<ProfileContext | null>(null);

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const {
    data: profileCompletion,
    isFetching: isFetchingProfileCompletion,
    refetch: refetchProfileCompletion,
  } = useGetProfileCompletion({
    enabled: !!user,
  });

  const profileCompletionPercentage = useMemo(() => {
    if (!profileCompletion) return 0;
    const { profileCompletionPoints, profileCompletionRequiredPoints } =
      profileCompletion;
    return Math.round(
      (profileCompletionPoints / profileCompletionRequiredPoints) * 100,
    );
  }, [profileCompletion]);

  const [managementYear] = useSessionStorage(
    "management-year",
    new Date().getFullYear(),
  );

  const [lastVisitedTab, setLastVisitedTab] = useState<ProfileTab>("general");
  const [lastVisitedYearInTab, setLastVisitedYearInTab] = useState<
    Record<ProfileTab, number>
  >({
    general: managementYear,
    socialSecurity: managementYear - 1,
    fiscal: managementYear,
    accounting: managementYear - 1,
  });

  useEffect(() => {
    setLastVisitedYearInTab({
      general: managementYear,
      socialSecurity: managementYear - 1,
      fiscal: managementYear,
      accounting: managementYear - 1,
    });
  }, [managementYear]);

  const handlers = useMemo(() => {
    const handleSetLastVisitedTab = (tab: ProfileTab) => {
      setLastVisitedTab(tab);
    };

    const handleSetLastVisitedYearInTab = (tab: ProfileTab, year: number) => {
      setLastVisitedYearInTab((prev) => ({
        ...prev,
        [tab]: year,
      }));
    };

    return {
      handleSetLastVisitedTab,
      handleSetLastVisitedYearInTab,
    };
  }, []);

  const resetProfile = useCallback(() => {
    setLastVisitedTab("general");
    setLastVisitedYearInTab({
      general: managementYear,
      socialSecurity: managementYear - 1,
      fiscal: managementYear,
      accounting: managementYear - 1,
    });
  }, [managementYear]);

  useEffect(() => {
    if (user) {
      refetchProfileCompletion();
    }
  }, [user, refetchProfileCompletion]);

  return (
    <ProfileContext.Provider
      value={{
        minimumYear:
          profileCompletion?.minAllowedYear || new Date().getFullYear(),
        lastVisitedTab,
        lastVisitedYearInTab,
        profileCompletion,
        profileCompletionPercentage,
        isFetchingProfileCompletion,
        setLastVisitedTab: handlers.handleSetLastVisitedTab,
        setLastVisitedYearInTab: handlers.handleSetLastVisitedYearInTab,
        resetProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfileProvider() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfileProvider must be used within ProfileProvider");
  }
  return context;
}
